/**
 * Permissões por papel na organização (fonte única da matriz role → flags).
 *
 * Uso:
 * - Lógica / dados / props: `const { canX } = usePermissions()` + if/efeitos.
 * - Esconder UI: `{canX && <Componente />}`.
 *
 * Segurança real: backend + RLS. Aqui é apenas UX.
 */
import { useMemo } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';

export interface Permissions {
    // Roles
    isOwner: boolean;
    isAdmin: boolean;
    isAgent: boolean;
    isMember: boolean;

    // Permissões de Membros
    canInviteMembers: boolean;
    canRemoveMembers: boolean;
    canViewMembers: boolean;
    canManageContacts: boolean;

    // Permissões de Organização
    canEditOrganization: boolean;
    canDeleteOrganization: boolean;
    canManageSubscription: boolean;
    /** Página de faturamento — owner/admin; member usa o plano da organização */
    canViewBilling: boolean;
    canManagePixKey: boolean;

    // Permissões de Leads
    canCreateLeads: boolean;
    canEditLeads: boolean;
    canDeleteLeads: boolean;
    canViewAllLeads: boolean;
    canAssignLeads: boolean;
    /** Filtro por agente (Kanban, tarefas) — member só vê os próprios via RLS */
    canFilterByAgent: boolean;

    // Permissões de Tasks
    canCreateTasks: boolean;
    canEditTasks: boolean;
    canDeleteTasks: boolean;
    canViewAllTasks: boolean;

    // Permissões de Funis
    canCreateFunnels: boolean;
    canEditFunnels: boolean;
    canDeleteFunnels: boolean;
    canManageStages: boolean;

    // Permissões de Chat
    canSendMessages: boolean;
    canViewAllConversations: boolean;
    canManageIntegrations: boolean;

    // Permissões de Relatórios
    canViewReports: boolean;
    canExportData: boolean;
}

const NO_PERMISSIONS: Permissions = {
    isOwner: false,
    isAdmin: false,
    isAgent: false,
    isMember: false,
    canInviteMembers: false,
    canRemoveMembers: false,
    canViewMembers: false,
    canManageContacts: false,
    canEditOrganization: false,
    canDeleteOrganization: false,
    canManageSubscription: false,
    canViewBilling: false,
    canManagePixKey: false,
    canCreateLeads: false,
    canEditLeads: false,
    canDeleteLeads: false,
    canViewAllLeads: false,
    canAssignLeads: false,
    canFilterByAgent: false,
    canCreateTasks: false,
    canEditTasks: false,
    canDeleteTasks: false,
    canViewAllTasks: false,
    canCreateFunnels: false,
    canEditFunnels: false,
    canDeleteFunnels: false,
    canManageStages: false,
    canSendMessages: false,
    canViewAllConversations: false,
    canManageIntegrations: false,
    canViewReports: false,
    canExportData: false,
};

export const usePermissions = (): Permissions => {
    const { currentOrganization } = useOrganization();

    return useMemo<Permissions>(() => {
        const role = currentOrganization?.role;
        if (!role) return NO_PERMISSIONS;

        const isOwner = role === 'owner';
        const isAdmin = role === 'admin';
        // @ts-ignore - False positive: role type is correctly inferred
        const isAgent = role === 'agent';
        const isMember = role === 'member';

        return {
            isOwner,
            isAdmin,
            isAgent,
            isMember,

            canInviteMembers: isOwner || isAdmin,
            canRemoveMembers: isOwner,
            canViewMembers: true,
            canManageContacts: isOwner || isAdmin,

            canEditOrganization: isOwner,
            canDeleteOrganization: isOwner,
            canManageSubscription: isOwner,
            canViewBilling: isOwner || isAdmin,
            canManagePixKey: isOwner || isAdmin,

            canCreateLeads: true,
            canEditLeads: true,
            canDeleteLeads: isOwner || isAdmin,
            canViewAllLeads: isOwner || isAdmin,
            canAssignLeads: isOwner || isAdmin,
            canFilterByAgent: !isMember,

            canCreateTasks: true,
            canEditTasks: true,
            canDeleteTasks: isOwner || isAdmin,
            canViewAllTasks: isOwner || isAdmin,

            canCreateFunnels: isOwner || isAdmin,
            canEditFunnels: isOwner || isAdmin,
            canDeleteFunnels: isOwner,
            canManageStages: isOwner || isAdmin,

            canSendMessages: true,
            canViewAllConversations: isOwner || isAdmin,
            canManageIntegrations: isOwner || isAdmin,

            canViewReports: isOwner || isAdmin,
            canExportData: isOwner || isAdmin,
        };
    }, [currentOrganization?.role]);
};
